import { useState, useMemo, useEffect } from 'react';
import {
  Search,
  ArrowUpDown,
  Filter,
  RussianRuble,
  FileText,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { Button } from './ui/button';
import { API_URL } from '../config';
import { authFetch } from '../utils/authFetch';


export function MyStatistic() {
  const [analyticsStats, setAnalyticsStats] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('earnings');
  const [hasReferrer, setHasReferrer] = useState(false);

  const periods = [
    { id: 'day',   label: 'День' },
    { id: 'week',  label: 'Неделя' },
    { id: 'month', label: 'Месяц' },
    { id: 'year',  label: 'Год' },
    { id: 'all',   label: 'Весь период' },
    { id: 'custom',label: 'Свои даты' },
  ];

  const [period, setPeriod] = useState('all');
  const today = new Date().toISOString().slice(0, 10);
  const [customDates, setCustomDates] = useState({ from: today, to: today });


  // когда меняется period или customDates, пересчитываем earningsData:
  useEffect(() => {
    let from, to = new Date();
    switch (period) {
      case 'day':
        from = new Date(Date.now() - 24*3600*1000);
        break;
      case 'week':
        from = new Date(Date.now() - 7*24*3600*1000);
        break;
      case 'month':
        from = new Date();
        from.setMonth(from.getMonth() - 1);
        break;
      case 'year':
        from = new Date();
        from.setFullYear(from.getFullYear() - 1);
        break;
      case 'all':
        from = new Date("1970-01-01"); // посылаем ВСЕГДА
        break;
      case 'custom':
        from = customDates.from ? new Date(customDates.from) : null;
        to   = customDates.to   ? new Date(customDates.to)   : to;
        break;
    }
    const qs = [];
    qs.push(`from=${from.toISOString().slice(0, 10)}`);
    qs.push(`to=${to.toISOString().slice(0, 10)}`);
    const url = `${API_URL}/analytics/documents${qs.length? '?'+qs.join('&') : ''}`;
    const jwt = localStorage.getItem('token');
    authFetch(url, {
            headers: {
              'Authorization': `Bearer ${jwt}`
            }})
      .then(res => res.json())
      .then(data => {
        setAnalyticsStats(data.analyticsStats);
        setEarningsData(data.earningsData);
      });
  }, [period, customDates]);

  const hasRef = async () => {
    try {
      const res = await authFetch(`${API_URL}/has-ref`, {
      });
      const data = await res.json();
      if (data.hasReferrer) {
        setHasReferrer(true);
      }
      else {
        setHasReferrer(false);
        console.log('No referrer found');
      }
      return data.hasReferrer;
    } catch (err) {
      console.error('Error checking referrer:', err);
    }
  }

  useEffect(() => {
    hasRef();
  }, []);


  const totalEarnings = useMemo(
    () => analyticsStats.reduce((sum, s) => sum + s.sales_count * s.price, 0),
    [analyticsStats]
  );
  const totalSales = useMemo(
    () => analyticsStats.reduce((sum, s) => sum + s.sales_count, 0),
    [analyticsStats]
  );


  return (
    <div className="p-6 sm:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Overview cards */}
      {/* Header */}
            <div className="text-center space-y-2 mb-6">
              <h1 className="gradient-text text-2xl sm:text-3xl font-bold">Аналитика</h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Отслеживайте доходы и статистику продаж ваших документов
              </p>
            </div>

      <div className="flex">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Период" />
          </SelectTrigger>
          <SelectContent>
            {periods.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {period === 'custom' && (
          <>
            <Input
              type="date"
              value={customDates.from}
              onChange={e =>
                setCustomDates(d => ({ ...d, from: e.target.value }))
              }
              className="w-36"
            />
            <Input
              type="date"
              value={customDates.to}
              onChange={e =>
                setCustomDates(d => ({ ...d, to: e.target.value }))
              }
              className="w-36"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <div className="bg-white p-3 sm:p-4 rounded-lg border">
                <div className="flex items-center gap-2">
            <RussianRuble className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            <span className="text-sm text-gray-600">Общий доход</span>
          </div>
          <p className="text-2xl mt-2">{totalEarnings}₽</p>
        </div>
        <div className="bg-white p-3 sm:p-4 rounded-lg border">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  <span className="text-xs sm:text-sm text-gray-600">Продано документов</span>
                </div>
          <p className="text-2xl mt-2">{totalSales}</p>
        </div>
      </div>

      {/* Line chart */}
      <div className="bg-white rounded-lg border">
        
      <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2 text-base">
            <RussianRuble className="h-5 w-5" /> Статистика дохода
          </h3>
      </div>

      <div className="p-6 h-64">
  <ResponsiveContainer width="100%" height="100%">
    {earningsData[0] ? (
        <LineChart data={earningsData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="day"
          tickFormatter={(value) => {
            return value.slice(0, 10);
          }}
        />
        <YAxis />
        <Tooltip
          formatter={(value, name) =>
            name === 'Доход'
              ? [`${value}₽`, 'Доход']
              : [`${value}`, 'Скачивания']
          }
        />
        <Line
          type="monotone"
          dataKey="earnings"
          stroke="#2563eb"
          strokeWidth={2}
          dot={{ r: 3, strokeWidth: 2 }}
          name="Доход"
        />
        <Line
          type="monotone"
          dataKey="downloads"
          stroke="#22c55e" // зелёный
          strokeWidth={2}
          dot={{ r: 3, strokeWidth: 2 }}
          name="Скачивания"
        />
      </LineChart>
    ) : (
      <p className='text-red sm:text-sm'>
        Нет документов за этот период
      </p>
    )}
  
</ResponsiveContainer>

</div>

              </div>



      {/* Analytics table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 sm:p-6 border-b">
          <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                  Статистика по документам
                </h3>
        </div>
        <div className="p-3 sm:p-6">
            <div className="flex flex-col gap-3 sm:gap-4 mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Поиск по названию документа..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 text-sm"
                    />
                  </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="approved">Активные</SelectItem>
                <SelectItem value="pending">На модерации</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Сортировка" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="earnings">Доход</SelectItem>
                  <SelectItem value="sales">Продажи</SelectItem>
                  {/* <SelectItem value="views">Просмотры</SelectItem> */}
                  <SelectItem value="price">Цена</SelectItem>
                  <SelectItem value="title">Название</SelectItem>
                </SelectContent>
              </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="w-full sm:w-auto"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto -mx-3 sm:mx-0 p-3 sm:p-6">
          <div className="min-w-[600px] px-3 sm:px-0">
            <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left">Документ</th>
                <th className="p-3 text-left">Цена</th>
                <th className="p-3 text-left">Продажи</th>
                {/* <th className="p-3 text-left">Просмотры</th> */}
                <th className="p-3 text-left">Доход</th>
                <th className="p-3 text-left">Комиссия + налоги</th>
                <th className="p-3 text-left">Статус</th>
              </tr>
            </thead>
            <tbody>
              {analyticsStats
                .filter(d =>
                  d.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                  (statusFilter === 'all' || d.status === statusFilter)
                )
                .sort((a, b) =>
                  sortOrder === 'asc'
                    ? a[sortBy] - b[sortBy]
                    : b[sortBy] - a[sortBy]
                )
                .map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="p-3 flex items-center gap-2">
                      <FileText className="h-5 w-5" /> {doc.title}
                    </td>
                    <td className="p-3">{doc.price}₽</td>
                    <td className="p-3">{doc.sales_count}</td>
                    {/* <td className="p-3">{doc.views_count}</td> */}
                    <td className="p-3 text-green-700">
                      {(doc.earnings - (hasRef ? doc.earnings * 0.35 + doc.earnings * 0.05 / 0.85 - doc.earnings * 0.05 : doc.earnings * 0.3)).toLocaleString()}₽
                    </td>
                    <td className="p-3 text-green-700">
                      {(hasRef ? doc.earnings * 0.35 + doc.earnings * 0.05 / 0.85 - doc.earnings * 0.05 : doc.earnings * 0.3).toLocaleString()}₽
                    </td>
                    <td className="p-3">
                      {doc.status === 'approved' ? (
                        <Badge className="bg-green-100 text-green-800">
                          Активен
                        </Badge>
                      ) : doc.status === 'rejected' ? (
                        <Badge className="bg-green-100 text-red-800">
                          Отклонен
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-yellow-800">
                          На модерации
                        </Badge>
                      )
                      }
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>
  );
}